<script setup lang="ts">
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { computed, onBeforeMount, ref } from "vue";
import StorySelectionPopup from "@/components/mobile/popup/StorySelectionPopup.vue";
import StoryListItem from "@/components/mobile/story/story-chapter/StoryListItem.vue";
import { Story } from "@/types/entity";
import ToneSelectionPopup from "@/components/mobile/popup/ToneSelectionPopup.vue";
import { useTone } from "@/stores/tone";
import { showLoadingToast } from "vant";
import { useTask } from "@/stores/task";
import { useRoute, useRouter } from "vue-router";
import { useStory } from "@/stores/story";
import i18n from "@/locales";

// State
const BGM_ENUM = [
  {
    text: i18n.global.t("bgm.happy"),
    value: 2,
  },
  {
    text: i18n.global.t("bgm.sad"),
    value: 3,
  },
  {
    text: i18n.global.t("bgm.angryIntense"),
    value: 1,
  },
  {
    text: i18n.global.t("bgm.fearfulAnxious"),
    value: 4,
  },
  {
    text: i18n.global.t("bgm.gentleSoothing"),
    value: 5,
  },
  {
    text: i18n.global.t("bgm.exciting"),
    value: 6,
  },
];
const showStorySelection = ref(false);
const selectedStory = ref<Story | null>(null);
const selectedStoryId = computed(() => {
  return selectedStory.value?.id ?? 0;
});

const toneStore = useTone();
const showToneSelection = ref(false);
const selectedToneId = ref<number>(0);
const selectedTone = computed(() => {
  return toneStore.getToneById(selectedToneId.value);
});

const selectedBgmId = ref<number>(0);
const canCreate = computed(() => {
  return (
    selectedStoryId.value > 0 &&
    selectedToneId.value > 0 &&
    selectedBgmId.value > 0
  );
});

// LifeCycle
const route = useRoute();
const loading = ref(false);
const storyUuid = route.query.storyUuid;
const storyStore = useStory();
const prepareData = async () => {
  loading.value = true;
  try {
    if (storyUuid) {
      selectedStory.value = await storyStore.getStoryItem(storyUuid as string);
    }
  } finally {
    loading.value = false;
  }
};
onBeforeMount(() => {
  prepareData();
});
// EventHandler
const taskStore = useTask();
const router = useRouter();
const onCreateVideoTask = async () => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.creatingVideoTask"),
  });
  try {
    await taskStore.createStoryVideo({
      storyId: selectedStoryId.value,
      isAudio: 0,
      mateToneId: selectedToneId.value,
      toneMood: selectedBgmId.value,
    });
    router.push({ name: "imageGenerateList", hash: "#unfinished" });
  } finally {
    loading.close();
  }
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <threads-nav :show-back="true" :enable-switch-mate="false">
      <template #center>
        <div></div>
      </template>
      <template #right>
        <button
          class="btn btn-primary btn-sm rounded"
          :disabled="!canCreate"
          @click="onCreateVideoTask"
        >
          {{ $t("Buttons.generateVideo") }}
        </button>
      </template>
    </threads-nav>
    <div class="p-2 flex flex-col gap-4 h-full overflow-y-auto">
      <div class="flex flex-col gap-2">
        <div class="text-base font-bold">
          {{ $t("story.selectStoryChapters") }}
        </div>
        <div
          class="flex flex-col items-center justify-center gap-2 p-4 border border-primary text-base-content/50"
          v-if="!selectedStory"
          @click="showStorySelection = true"
        >
          {{ $t("story.generateVideoText") }}
          <button class="btn btn-primary btn-sm rounded btn-wide">
            {{ $t("Buttons.select") }}
          </button>
        </div>
        <story-list-item
          v-else
          :story="selectedStory"
          class="border border-primary px-2"
          @click="showStorySelection = true"
        />
      </div>
      <div class="flex flex-col gap-2">
        <div class="text-base font-bold">{{ $t("story.narratorVoice") }}</div>
        <div
          v-if="!selectedTone"
          class="flex flex-col items-center justify-center gap-2 p-4 border border-primary text-base-content/50"
          @click="showToneSelection = true"
        >
          {{ $t("story.aNarratorVoice") }}
          <button class="btn btn-primary btn-sm rounded btn-wide">
            {{ $t("Buttons.select") }}
          </button>
        </div>
        <div
          v-else
          class="flex justify-between items-center border p-2 rounded"
          @click="showToneSelection = true"
        >
          <div class="flex gap-2 items-center">
            {{ selectedTone!.name }}
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <div class="text-base font-bold">{{ $t("story.backgroundMusic") }}</div>
        <div class="flex flex-col gap-2">
          <div
            class="flex items-center gap-2 border border-primary p-2 rounded"
            :class="{
              'bg-primary': selectedBgmId === item.value,
              'text-primary': selectedBgmId === item.value,
            }"
            v-for="item in BGM_ENUM"
            :key="item.value"
            @click="selectedBgmId = item.value"
          >
            <input
              class="radio radio-xs"
              type="radio"
              :checked="selectedBgmId === item.value"
            />
            <span class="text-base-content/50">{{ item.text }}</span>
          </div>
        </div>
      </div>
    </div>
    <story-selection-popup
      v-model:show="showStorySelection"
      v-model:selected-story="selectedStory"
    />
    <tone-selection-popup
      v-model:show="showToneSelection"
      v-model:selected-tone-id="selectedToneId"
    />
  </div>
</template>
